import { parseISO } from "date-fns";

import { ISODate } from "../types/common";
import { InvoiceStatus } from "../types/invoice";
import {
    PaymentStatus,
    PreAndPaymentStateWithDocuments,
    PaymentOrderStatus,
} from "../types/payment";
import { UserRole } from "../types/userCurrent";

import { isUserFinanceDepartment, isUserPaymentApprover } from "./role";
import { ApproveStatus, ApproverDetails } from "../types/claim";

export const hasPreAndPaymentAction = (
    roles: UserRole[],
    preSubmitPaymentStatus?: PaymentOrderStatus,
    paymentStatus?: PaymentStatus | null,
    invoiceStatus?: InvoiceStatus,
    currentApprover?: ApproverDetails,
    isPreviousPriorityApproverApproved?: boolean | null,
): boolean => {
    if (invoiceStatus && invoiceStatus !== InvoiceStatus.FINANCE_APPROVED) {
        return false;
    }

    if (preSubmitPaymentStatus === PaymentOrderStatus.PARTIAL_APPROVED) {
        if (
            currentApprover?.status === ApproveStatus.PENDING &&
            isPreviousPriorityApproverApproved
        ) {
            return true;
        }
        return hasPaymentAction(roles, paymentStatus);
    } else {
        return (
            hasPaymentOrderHasAction(
                roles,
                preSubmitPaymentStatus,
                isPreviousPriorityApproverApproved,
            ) || hasPaymentAction(roles, paymentStatus)
        );
    }
};

// Whether the approvers in front of the current approver have completed their actions
export const hasBeforeCurrentApproversHaveAction = (
    currentApprover: ApproverDetails,
    paymentOrderApprovers?: ApproverDetails[],
): boolean => {
    if (paymentOrderApprovers) {
        const currentApproverIndex = paymentOrderApprovers.findIndex(
            (it) => it.details.email === currentApprover.details.email,
        );
        return paymentOrderApprovers
            .slice(0, currentApproverIndex)
            .every(
                (approver) =>
                    approver.status === ApproveStatus.APPROVED ||
                    approver.status === ApproveStatus.REJECTED,
            );
    }
    return false;
};

export const hasPaymentOrderHasAction = (
    roles: UserRole[],
    paymentOrderStatus?: PaymentOrderStatus,
    isPreviousPriorityApproverApproved?: boolean | null,
): boolean => {
    const isFirstApprover = isPreviousPriorityApproverApproved === null;
    switch (paymentOrderStatus) {
        case PaymentOrderStatus.CREATED:
            if (!isFirstApprover) {
                return !!isPreviousPriorityApproverApproved; // Check first approver invalid
            }
            return isUserPaymentApprover(roles);
        case PaymentOrderStatus.PARTIAL_APPROVED:
            if (!isFirstApprover) {
                return (
                    !!isPreviousPriorityApproverApproved &&
                    isUserPaymentApprover(roles)
                );
            }
            return false;
        case PaymentOrderStatus.REJECTED:
            return isUserFinanceDepartment(roles);
        default:
            return false;
    }
};

export const hasPaymentAction = (
    roles: UserRole[],
    paymentStatus?: PaymentStatus | null,
): boolean => {
    switch (paymentStatus) {
        case PaymentStatus.FAILED:
            return isUserFinanceDepartment(roles);
        default:
            return false;
    }
};

export const comparePaymentDateDesc = (
    a: PreAndPaymentStateWithDocuments,
    b: PreAndPaymentStateWithDocuments,
): number =>
    comparePaymentValueDateDateDesc(
        a.paymentOrderState.props,
        b.paymentOrderState.props,
    );

export const comparePaymentValueDateDateDesc = (
    a: { valueDate: ISODate },
    b: { valueDate: ISODate },
): number => {
    const aDate = parseISO(a.valueDate);
    const bDate = parseISO(b.valueDate);
    if (aDate > bDate) {
        return -1;
    } else if (bDate > aDate) {
        return 1;
    } else {
        return 0;
    }
};

export const comparePaymentEntryDateDateDesc = (
    a: { entryDate: ISODate },
    b: { entryDate: ISODate },
): number => {
    const aDate = parseISO(a.entryDate);
    const bDate = parseISO(b.entryDate);
    if (aDate > bDate) {
        return -1;
    } else if (bDate > aDate) {
        return 1;
    } else {
        return 0;
    }
};
